import React, { forwardRef, useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import html2canvas from 'html2canvas';
import confetti from 'canvas-confetti';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import PremiumModal from './PremiumModal';
import { getDecorationsForQuote, generatePositions } from '../utils/decorationUtils';
import supabase from '../supabase/supabase';

const { FiDownload, FiCheck, FiChevronDown, FiLock, FiLoader, FiCheckCircle } = FiIcons;

const RESOLUTIONS = [
  { id: 'HD', label: 'HD (720p)', width: 720, height: 1280, premium: false },
  { id: 'FHD', label: 'Full HD (1080p)', width: 1080, height: 1920, premium: false },
  { id: '2K', label: '2K (1440p)', width: 1440, height: 2560, premium: true },
  { id: '4K', label: '4K (Ultra HD)', width: 2160, height: 3840, premium: true },
];

const applyTransform = (text, mode) => {
  if (!text || mode === 'layout') return text;
  if (mode === 'uppercase') return text.toUpperCase();
  if (mode === 'lowercase') return text.toLowerCase();
  if (mode === 'camelcase') return text.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  return text;
};

// --- DESIGN ENGINE ---
const DynamicTypography = ({ text, fontSize, textColor, accentColor, typographyType, textAlign, textTransform, customizations }) => {
  const isTransformed = textTransform !== 'layout';
  const transformedText = applyTransform(text, textTransform);
  const seed = customizations?.dynamicSeed || 0;
  
  // CRITICAL: Strict Detection for single-line text only
  // We check for absence of newlines and a reasonable length for a "single line" heading
  const isSingleLine = !text.includes('\n') && text.length < 60;
  const shiftData = isSingleLine ? { 'data-canvas-fix': 'single-line' } : {};
  
  // Inline-block + Relative is required for 'top' to work in html2canvas
  const fixBaseStyle = isSingleLine ? { 
    display: 'inline-block', 
    position: 'relative',
    verticalAlign: 'middle'
  } : {};

  const baseStyle = {
    fontSize: `${fontSize}px`,
    color: textColor,
    textAlign: textAlign || 'center',
    lineHeight: '1.1',
    width: 'auto',
    maxWidth: '100%',
    boxSizing: 'border-box',
    fontWeight: '500',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    display: 'inline-block'
  };

  switch (typographyType) {
    case 'ribbon': {
      const lines = transformedText.split('\n');
      const ribbonChunks = [];
      lines.forEach((line, lineIdx) => {
        const ribbonWords = line.trim().split(' ').filter(w => w !== '');
        if (ribbonWords.length === 0) {
          if (lineIdx < lines.length - 1) ribbonChunks.push(""); 
          return;
        }
        const wordsPerChunk = 2 + Math.floor(Math.abs(Math.sin(seed * 10)) * 3);
        for (let i = 0; i < ribbonWords.length; i += wordsPerChunk) {
          ribbonChunks.push(ribbonWords.slice(i, i + wordsPerChunk).join(' '));
        }
      });

      const rawColors = ['#38bdf8', '#818cf8', '#fb7185', '#fbbf24', '#10b981'];
      const colorOffset = Math.floor(seed * rawColors.length);

      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', width: '100%', boxSizing: 'border-box' }}>
          {ribbonChunks.map((chunk, idx) => (
            chunk === "" ? <div key={`empty-${idx}`} style={{ height: '16px' }} /> : (
              <div key={`${idx}-${seed}`} style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0 24px', boxSizing: 'border-box' }}>
                <div style={{ padding: '12px 24px', transform: 'skewX(-10deg)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', backgroundColor: rawColors[(idx + colorOffset) % rawColors.length], display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', minHeight: `${fontSize * 1.5}px` }}>
                  <span {...shiftData} style={{ ...baseStyle, transform: 'skewX(10deg)', textTransform: isTransformed ? 'none' : 'uppercase', fontWeight: 'bold', letterSpacing: '0.05em', ...fixBaseStyle }}>
                    {applyTransform(chunk, 'layout')} 
                  </span>
                </div>
              </div>
            )
          ))}
        </div>
      );
    }

    case 'grunge': {
      const lines = transformedText.split('\n');
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '95%', margin: '0 auto', gap: '16px', boxSizing: 'border-box' }}>
          {lines.map((line, lineIdx) => (
            <div key={`${lineIdx}-${seed}`} style={{ position: 'relative', zIndex: 10, padding: '8px', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: '8px 12px', width: '100%', boxSizing: 'border-box' }}>
              {line.split(' ').map((word, idx) => {
                const wordSeed = Math.abs(Math.sin(idx * 1.5 + seed * 100 + lineIdx));
                return (
                  <span {...shiftData} key={`${idx}-${seed}`} style={{ fontSize: `${fontSize * (0.9 + wordSeed * 0.7)}px`, color: wordSeed > 0.7 ? accentColor : textColor, textTransform: isTransformed ? 'none' : 'uppercase', transform: `rotate(${(wordSeed - 0.5) * 15}deg)`, opacity: 0.8 + (wordSeed * 0.2), fontWeight: 'bold', lineHeight: 1, transition: 'all 0.3s', ...fixBaseStyle }}>
                    {applyTransform(word, 'layout')}
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      );
    }

    case 'dynamic': {
      const lines = transformedText.split('\n');
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '95%', margin: '0 auto', gap: '16px', boxSizing: 'border-box' }}>
          {lines.map((line, lineIdx) => (
            <div key={`${lineIdx}-${seed}`} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: '12px 16px', padding: '0 32px', width: '100%', boxSizing: 'border-box' }}>
              {line.split(' ').map((word, idx) => {
                const wordSeed = Math.abs(Math.sin(idx * 7.5 + seed * 100 + lineIdx));
                const sizeScale = 0.8 + (wordSeed * 1.0);
                return (
                  <span {...shiftData} key={`${idx}-${seed}`} style={{ ...baseStyle, fontSize: `${fontSize * sizeScale}px`, fontWeight: wordSeed > 0.35 ? '700' : '400', color: wordSeed > 0.6 ? accentColor : textColor, transform: `rotate(${(wordSeed - 0.5) * 14}deg)`, lineHeight: '1', padding: '4px 6px', boxSizing: 'border-box', ...fixBaseStyle }} >
                    {word}
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      );
    }

    case 'brutal': {
      const brutalRotate = (Math.sin(seed * 50) * 4).toFixed(1);
      return (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0 24px', boxSizing: 'border-box' }}>
          <div style={{ ...baseStyle, fontSize: `${fontSize * 1.2}px`, color: '#000', textAlign: 'left', transform: `rotate(${brutalRotate}deg)`, padding: '32px', backgroundColor: '#FFDC00', border: '5px solid black', boxShadow: '10px 10px 0px rgba(0,0,0,1)', fontWeight: '900', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: `${fontSize * 2}px` }}>
            <span {...shiftData} style={fixBaseStyle}>{transformedText}</span>
          </div>
        </div>
      );
    }

    case 'polaroid': {
      const polRotate = (Math.sin(seed * 40) * 3).toFixed(1);
      return (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0 40px', boxSizing: 'border-box' }}>
          <div style={{ ...baseStyle, fontSize: `${fontSize * 0.9}px`, color: '#1a1a1a', transform: `rotate(${polRotate}deg)`, backgroundColor: 'white', padding: '24px 24px 80px 24px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', border: '1px solid #f3f4f6', fontStyle: 'italic', boxSizing: 'border-box', lineHeight: '1.2' }}>
            <span {...shiftData} style={fixBaseStyle}>{transformedText}</span>
          </div>
        </div>
      );
    }

    case 'retro': {
      const glitchStrength = Math.abs(Math.sin(seed * 100)) * 8 + 2;
      const lineDensity = 2 + Math.floor(Math.abs(Math.cos(seed * 50)) * 6);
      return (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0 24px', boxSizing: 'border-box' }}>
          <div style={{ position: 'relative', padding: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', minHeight: `${fontSize * 2}px` }}>
            <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderRadius: '8px', overflow: 'hidden', borderTop: `2px solid ${accentColor}`, borderBottom: `2px solid ${accentColor}`, boxSizing: 'border-box' }}>
              <div style={{ position: 'absolute', inset: 0, opacity: 0.2, backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 1px, ${accentColor} 1px, ${accentColor} 2px)`, backgroundSize: `100% ${lineDensity}px` }}></div>
            </div>
            <div style={{ position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
              <h2 {...shiftData} style={{ ...baseStyle, fontSize: `${fontSize * 1.1}px`, color: '#ffffff', textShadow: `${glitchStrength}px 0px 0px #ff00ff, -${glitchStrength}px 0px 0px #00ffff`, textTransform: isTransformed ? 'none' : 'uppercase', lineHeight: '1', fontWeight: '900', letterSpacing: '0.2em', fontStyle: 'italic', margin: 0, display: 'block', ...fixBaseStyle }}>
                {transformedText}
              </h2>
            </div>
          </div>
        </div>
      );
    }

    case 'bordered': {
      const frameRadius = (Math.abs(Math.sin(seed * 30)) * 40).toFixed(0);
      const cornerSize = (20 + Math.abs(Math.cos(seed * 20)) * 30).toFixed(0);
      return (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0 32px', boxSizing: 'border-box' }}>
          <div style={{ position: 'relative', padding: '40px', border: `3px solid ${textColor}`, borderRadius: `${frameRadius}px`, margin: '16px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', minHeight: `${fontSize * 2}px` }}>
            <div style={{ position: 'absolute', top: '-16px', left: '-16px', borderTop: `4px solid ${accentColor}`, borderLeft: `4px solid ${accentColor}`, width: `${cornerSize}px`, height: `${cornerSize}px`, boxSizing: 'border-box' }} />
            <div style={{ position: 'absolute', bottom: '-16px', right: '-16px', borderBottom: `4px solid ${accentColor}`, borderRight: `4px solid ${accentColor}`, width: `${cornerSize}px`, height: `${cornerSize}px`, boxSizing: 'border-box' }} />
            <span {...shiftData} style={{ ...baseStyle, position: 'relative', zIndex: 10, ...fixBaseStyle }}>{transformedText}</span>
          </div>
        </div>
      );
    }

    case 'sticker': {
      const stickerRotate = (Math.sin(seed * 60) * 5).toFixed(1);
      const bgRotate = (Math.cos(seed * 60) * 8).toFixed(1);
      return (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0 24px', boxSizing: 'border-box' }}>
          <div style={{ position: 'relative', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', transform: `rotate(${stickerRotate}deg)`, boxSizing: 'border-box' }}>
            <div style={{ position: 'absolute', inset: 0, backgroundColor: 'white', borderRadius: '8px', boxShadow: '8px 8px 0px rgba(0,0,0,0.1)', transform: `rotate(${(parseFloat(bgRotate) + 2).toFixed(1)}deg)`, boxSizing: 'border-box' }}></div>
            <div style={{ ...baseStyle, position: 'relative', zIndex: 10, backgroundColor: '#ffffff', color: '#000000', padding: '32px', borderRadius: '8px', border: '3px solid black', textAlign: 'center', fontWeight: '900', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: `${fontSize * 1.8}px` }}>
              <span {...shiftData} style={fixBaseStyle}>{transformedText}</span>
            </div>
          </div>
        </div>
      );
    }

    case 'paint': {
      const blobRotate = (seed * 360).toFixed(0);
      return (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0 24px', boxSizing: 'border-box' }}>
          <div style={{ position: 'relative', padding: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
            <div style={{ position: 'absolute', inset: 0, opacity: 0.3, backgroundColor: accentColor || textColor, borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%', transform: `rotate(${blobRotate}deg) scale(${0.9 + (seed * 0.2)})`, boxSizing: 'border-box' }} />
            <span {...shiftData} style={{ ...baseStyle, position: 'relative', zIndex: 10, fontWeight: 'bold', fontStyle: 'italic', textAlign: 'center', ...fixBaseStyle }}>{transformedText}</span>
          </div>
        </div>
      );
    }

    case 'magazine': {
      const isRight = seed > 0.5;
      const borderThickness = Math.floor(Math.abs(Math.cos(seed * 30)) * 12) + 4;
      return (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0 32px', boxSizing: 'border-box' }}>
          <div style={{ ...baseStyle, padding: '0 40px', textAlign: isRight ? 'right' : 'left', borderLeft: !isRight ? `${borderThickness}px solid ${accentColor}` : 'none', borderRight: isRight ? `${borderThickness}px solid ${accentColor}` : 'none', boxSizing: 'border-box' }}>
            <span style={{ display: 'block', fontSize: '0.35em', textTransform: 'uppercase', letterSpacing: '0.5em', marginBottom: '16px', opacity: 0.6, fontWeight: '900' }}>Editorial Piece</span>
            <span {...shiftData} style={{ fontFamily: 'serif', fontWeight: '900', fontStyle: 'italic', display: 'block', fontSize: `${fontSize * 1.3}px`, lineHeight: '1', ...fixBaseStyle }}>
              {transformedText}
            </span>
          </div>
        </div>
      );
    }

    default: return (
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0 32px', boxSizing: 'border-box' }}>
        <div style={{ ...baseStyle, lineHeight: '1.2' }}>
          <span {...shiftData} style={fixBaseStyle}>{transformedText}</span>
        </div>
      </div>
    );
  }
};

const BackgroundDecorations = ({ quote, color, enabled, themeId }) => {
  const decorations = useMemo(() => getDecorationsForQuote(quote || '', themeId), [quote, themeId]);
  const positions = useMemo(() => generatePositions(24, (quote?.length || 0)), [quote]);
  
  if (!enabled) return null;
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', userSelect: 'none' }}>
      {positions.map((pos, i) => (
        <div 
          key={i} 
          style={{ 
            position: 'absolute', 
            top: pos.top, 
            left: pos.left, 
            transform: `rotate(${pos.rotate}) scale(${pos.scale})`, 
            opacity: pos.opacity, 
            fontSize: '40px', 
            color: color || 'white', 
            transition: 'all 0.5s ease-out',
            lineHeight: 1
          }} 
        >
          {decorations[i % decorations.length]}
        </div>
      ))}
    </div>
  );
};

const PosterPreview = forwardRef(({ quote, author, customizations, unlockedResolution, autoDownloadTrigger, onAutoDownloadComplete, onCloseUnlock }, ref) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedRes, setSelectedRes] = useState(RESOLUTIONS[1]);
  const [showResMenu, setShowResMenu] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  
  const isPortrait = customizations.orientation === 'portrait';
  const isSquare = customizations.orientation === 'square';

  useEffect(() => {
    if (unlockedResolution) {
      const res = RESOLUTIONS.find(r => r.id === unlockedResolution);
      if (res) {
        setSelectedRes(res);
        setShowSuccessDialog(true);
      }
    }
  }, [unlockedResolution]);

  useEffect(() => {
    if (autoDownloadTrigger && !isGenerating) {
      handleDownload();
      if (onAutoDownloadComplete) onAutoDownloadComplete();
    }
  }, [autoDownloadTrigger]);

  const handleDownload = async () => {
    if (!ref.current || isGenerating) return;
    setIsGenerating(true);
    setShowResMenu(false);
    
    try {
      await document.fonts.ready;
      const element = ref.current;
      const scale = selectedRes.width / element.offsetWidth;
      
      const canvas = await html2canvas(element, {
        scale: scale,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false,
        onclone: (clonedDoc, clonedElement) => {
           clonedElement.style.fontSmooth = 'always';
           clonedElement.style.webkitFontSmoothing = 'antialiased';
           
           // THE DEFINITIVE ALIGNMENT FIX V4
           // Find elements strictly tagged as single-line and force an aggressive upward shift.
           // We use -0.35em to pull the baseline up.
           const singleLineElements = clonedElement.querySelectorAll('[data-canvas-fix="single-line"]');
           singleLineElements.forEach(el => {
             el.style.setProperty('top', '-0.35em', 'important');
             el.style.setProperty('line-height', '0.8', 'important'); // Squeezes excess font height
           });
        }
      });
      
      canvas.toBlob((blob) => {
        if (!blob) throw new Error("Canvas failed");
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `quote-poster-${selectedRes.id}-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } });
        setIsGenerating(false);
      }, 'image/png', 1.0);
    } catch (err) {
      alert("Download failed. Try again.");
      setIsGenerating(false);
    }
  };

  const handlePremiumProceed = async (email) => {
    try {
      const paymentRef = `pay_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
      localStorage.setItem('pending_poster_design', JSON.stringify({ quote, author, customizations, resolution: selectedRes.id }));
      const { data, error } = await supabase.functions.invoke('checkout', { body: { email, resolution: selectedRes.id, paymentRef, origin: window.location.origin } });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="relative">
        <AnimatePresence>
          {isGenerating && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-[60] bg-white/90 backdrop-blur-xl flex flex-col items-center justify-center rounded-3xl border-4 border-indigo-500 shadow-2xl">
              <div className="w-20 h-20 border-8 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mb-6"></div>
              <h3 className="text-xl font-black text-indigo-900 uppercase tracking-tighter">Preparing High-Res File</h3>
            </motion.div>
          )}
          {showSuccessDialog && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
              <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 text-center relative">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Payment Verified!</h3>
                <p className="text-slate-500 mb-6 text-sm">Your <b>{selectedRes.id}</b> poster is ready. We've started your download automatically.</p>
                <div className="bg-amber-50 border border-amber-100 p-4 rounded-xl text-amber-700 text-xs font-semibold mb-6 flex gap-3 text-left">
                  <SafeIcon icon={FiLock} className="w-5 h-5 shrink-0" />
                  <span>Note: This is a one-time access. Please download before closing.</span>
                </div>
                <div className="flex flex-col gap-3">
                  <button onClick={handleDownload} className="w-full bg-indigo-600 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2">
                    <SafeIcon icon={FiDownload} className="w-5 h-5" /> Download Again
                  </button>
                  <button onClick={() => { setShowSuccessDialog(false); onCloseUnlock(); }} className="w-full bg-slate-100 text-slate-600 py-4 rounded-xl font-bold">Close</button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        
        <div className="bg-white rounded-3xl shadow-xl p-4 md:p-8 flex justify-center bg-slate-100/30">
          <div 
            ref={ref} 
            className={`relative overflow-hidden shadow-2xl flex flex-col ${isPortrait ? 'aspect-[9/16] w-full max-w-[340px]' : isSquare ? 'aspect-square w-full max-w-[400px]' : 'aspect-[16/9] w-full'}`} 
            style={{ 
              background: customizations.background.type === 'gradient' ? customizations.background.value : customizations.background.type === 'color' ? customizations.background.value : `url(${customizations.background.value}) center/cover`, 
              fontFamily: customizations.font 
            }}
          >
            <BackgroundDecorations quote={quote} color={customizations.textColor} enabled={customizations.showDecorations} themeId={customizations.decorationStyle} />
            
            <div 
              style={{ 
                position: 'relative', 
                zIndex: 10, 
                width: '100%', 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                padding: '48px 0',
                color: customizations.textColor,
                boxSizing: 'border-box'
              }}
            >
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', boxSizing: 'border-box' }}>
                <DynamicTypography 
                  text={quote} 
                  fontSize={isPortrait ? customizations.fontSize * 0.85 : customizations.fontSize} 
                  textColor={customizations.textColor} 
                  accentColor={customizations.accentColor} 
                  typographyType={customizations.typographyType} 
                  textAlign={customizations.textAlign} 
                  textTransform={customizations.textTransform} 
                  customizations={customizations} 
                />
              </div>
              
              {author && (
                <div style={{ marginTop: 'auto', padding: '24px 0', width: '100%', display: 'flex', justifyContent: 'center', boxSizing: 'border-box' }}>
                  <p style={{ fontWeight: '500', fontStyle: 'italic', opacity: 0.8, fontSize: `${Math.max(14, customizations.fontSize * 0.4)}px`, margin: 0, lineHeight: 1 }}>
                    — {author}
                  </p>
                </div>
              )}

              {!selectedRes.premium && (
                <div style={{ position: 'absolute', bottom: '16px', right: '16px', opacity: 0.4, userSelect: 'none', pointerEvents: 'none', display: 'flex', alignItems: 'center', gap: '6px', filter: 'grayscale(100%)' }}>
                  <span style={{ fontSize: '10px', fontWeight: '900', letterSpacing: '-0.05em', textTransform: 'uppercase' }}>quoteposter.app</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="relative col-span-1 sm:col-span-2 flex">
          <button 
            onClick={() => selectedRes.premium ? setShowPremiumModal(true) : handleDownload()}
            disabled={isGenerating}
            className="flex-1 flex items-center justify-center space-x-2 px-4 py-4 bg-indigo-600 text-white rounded-l-2xl hover:bg-indigo-700 disabled:opacity-50 transition-all font-black text-sm uppercase"
          >
            {isGenerating ? <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" /> : <SafeIcon icon={selectedRes.premium ? FiLock : FiDownload} className="w-4 h-4" />}
            <span>{isGenerating ? 'Processing...' : `Download ${selectedRes.id}`}</span>
          </button>
          <button 
            onClick={() => setShowResMenu(!showResMenu)}
            className="px-3 bg-indigo-600 text-white rounded-r-2xl border-l border-indigo-500/30 hover:bg-indigo-700 transition-colors"
          >
            <SafeIcon icon={FiChevronDown} className={`w-4 h-4 transition-transform ${showResMenu ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {showResMenu && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                className="absolute bottom-full mb-3 left-0 right-0 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-[70]"
              >
                {RESOLUTIONS.map((res) => (
                  <button 
                    key={res.id}
                    onClick={() => { setSelectedRes(res); setShowResMenu(false); }}
                    className={`w-full text-left px-4 py-4 text-xs font-bold flex items-center justify-between hover:bg-slate-50 ${selectedRes.id === res.id ? 'text-indigo-600 bg-indigo-50' : 'text-slate-600'}`}
                  >
                    <div className="flex items-center gap-2"><span>{res.label}</span>{res.premium && <SafeIcon icon={FiLock} className="w-3 h-3 text-amber-500" />}</div>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        <a 
          href="https://buymeacoffee.com/sumittiwari" 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center justify-center space-x-2 px-6 py-4 bg-[#FFDD00] text-black rounded-2xl hover:bg-[#ffea00] transition-all shadow-[0px_1px_2px_rgba(0,0,0,0.1),_0px_2px_4px_rgba(0,0,0,0.1)] border border-black/10 group overflow-hidden no-underline hover:no-underline"
          style={{ fontFamily: "var(--font-main), sans-serif" }}
        >
          <img src="https://cdn.buymeacoffee.com/buttons/bmc-new-btn-logo.svg" alt="Buy me a coffee" className="w-5 h-5 group-hover:scale-110 transition-transform" />
          <span className="text-xl font-bold leading-none" style={{ marginTop: '2px' }}>Buy me a coffee</span>
        </a>
      </div>

      <PremiumModal 
        isOpen={showPremiumModal} 
        onClose={() => setShowPremiumModal(false)} 
        onProceed={handlePremiumProceed}
        resolution={selectedRes.id}
      />
    </div>
  );
});

PosterPreview.displayName = 'PosterPreview';
export default PosterPreview;